<template>
    <NuxtLink
        :to="{ name: 'dobrovolnictvo-dobrovolnici-id', params: { id: volunteer.slug || volunteer.id } }"
        class="VolunteerRow group flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-surface-card"
    >
        <!-- Profile Image -->
        <div class="relative size-14 shrink-0 overflow-hidden rounded-full bg-gray-200 dark:bg-subtle">
            <img
                v-if="volunteer.profileImg"
                :src="volunteer.profileImg"
                :alt="fullName"
                class="size-full object-cover"
                loading="lazy"
            />
            <div v-else class="flex size-full items-center justify-center bg-gradient-to-br from-yellow-400/20 to-yellow-500/30 dark:from-yellow-400/30 dark:to-yellow-500/40">
                <span class="text-xl font-bold text-yellow-600/60 dark:text-yellow-400/70">{{ nameInitial }}</span>
            </div>
        </div>

        <!-- Main Content -->
        <div class="min-w-0 flex-1">
            <!-- Top row: Name and Location -->
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h3 class="truncate text-base font-semibold text-gray-900 dark:text-white">{{ fullName }}</h3>
                <div v-if="location" class="flex items-center gap-1 text-sm text-gray-500 dark:text-txt-body">
                    <Icon name="heroicons:map-pin" class="size-3.5" />
                    <span class="truncate">{{ location }}</span>
                </div>
            </div>

            <!-- Current Job -->
            <p v-if="currentJob" class="mt-0.5 truncate text-sm text-gray-600 dark:text-txt-body">
                {{ currentJob }}
            </p>

            <!-- Tags - Desktop only -->
            <div v-if="interests && interests.length" class="mt-2 hidden flex-wrap gap-1.5 sm:flex">
                <span
                    v-for="tag in interests.slice(0, tagsMaxCount)"
                    :key="tag"
                    class="inline-block rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                >
                    {{ tag }}
                </span>
                <span v-if="interests.length > tagsMaxCount" class="px-1 text-xs text-gray-400 dark:text-txt-body">
                    +{{ interests.length - tagsMaxCount }}
                </span>
            </div>
        </div>

        <!-- Action Arrow -->
        <div class="hidden shrink-0 items-center gap-2 sm:flex">
            <span class="text-sm font-medium text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-400">
                Zobraziť profil
            </span>
            <Icon name="heroicons:chevron-right" class="size-5 text-gray-400 dark:text-txt-body transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500" />
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import orderBy from 'lodash-es/orderBy'
import type { UserProfile } from '#shared/types/User'
import { getFullName } from '#shared/utils'
import { useUser } from '@/composables/user/useUser'

interface Props {
    volunteer: UserProfile
    tagsMaxCount?: number
}

const props = withDefaults(defineProps<Props>(), {
    tagsMaxCount: 4
})

const { formatLocation } = useUser()

const fullName = computed(() => getFullName(props.volunteer))

const nameInitial = computed(() => props.volunteer?.firstName?.charAt(0) || '?')

const currentJob = computed(() => {
    return orderBy(props.volunteer?.jobs ?? [], 'endYear', 'desc')[0]?.position || ''
})

const interests = computed(() => props.volunteer?.volunteerProfile?.interests ?? [])

const location = computed(() => formatLocation(props.volunteer))
</script>
